import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, AlertCircle, AudioLines } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface CustomSecureAudioPlayerProps {
  /** Unique identifier — used for localStorage play-count key */
  id: string;
  /** Attempt identifier — used to scope the localStorage play-count key so retakes are fresh */
  attemptId?: string | null;
  /** Raw audio URL (may be an S3 key). If resolveUrl is provided, it will be resolved first. */
  url: string;
  /** Optional async function that resolves the raw src into a playable (signed) URL */
  resolveUrl?: (rawUrl: string) => Promise<string>;
  /** Maximum number of times the audio can be played. 0 = unlimited. Default: 1 */
  playLimit?: number;
  /** Playback speed multiplier. Default: 1.0 */
  playbackRate?: number;
  /** Called when audio ends (used to mark audio as played in parent blur tracking) */
  onEnded?: () => void;
  className?: string;
}

export const CustomSecureAudioPlayer: React.FC<CustomSecureAudioPlayerProps> = ({
  id,
  attemptId,
  url,
  resolveUrl,
  playLimit = 1,
  playbackRate = 1,
  onEnded,
  className,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playCount, setPlayCount] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  // ── Restore play count from localStorage (survives page refreshes) ──────────
  useEffect(() => {
    if (id) {
      const keySuffix = attemptId ? `${attemptId}_${id}` : id;
      const stored = localStorage.getItem(`secure_audio_play_count_${keySuffix}`);
      setPlayCount(stored ? Number(stored) : 0);
    }
  }, [id, url, attemptId]);

  // ── Apply playback rate whenever it changes ─────────────────────────────────
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, isLoaded]);

  const isLimitReached = playLimit > 0 && playCount >= playLimit;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  // ── Load & play (resolving signed URL if needed) ────────────────────────────
  const handlePlay = useCallback(async () => {
    if (!audioRef.current) return;
    if (isLimitReached) {
      toast.warning("再生回数の上限に達しました。 / You have reached the maximum playback limit.");
      return;
    }
    if (isPlaying) return;

    try {
      hasFiredEnded.current = false;
      setIsResolving(true);
      let playableSrc = url;
      if (resolveUrl) {
        playableSrc = await resolveUrl(url);
      }
      // Only update src when needed (avoids re-loading on every play in unlimited mode)
      if (audioRef.current.src !== playableSrc) {
        audioRef.current.src = playableSrc;
        audioRef.current.load();
      }
      audioRef.current.playbackRate = playbackRate;
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (err) {
      console.error("Audio playback failed:", err);
      toast.error("音声を再生できませんでした。 / Could not play audio.");
    } finally {
      setIsResolving(false);
    }
  }, [url, resolveUrl, playbackRate, isLimitReached, isPlaying]);

  const handlePause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const handleTogglePlay = () => {
    if (isPlaying) return; // Enforce strict exam rules: no pausing
    else void handlePlay();
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoaded(true);
    }
  };

  const hasFiredEnded = useRef(false);

  const handleEnded = () => {
    setIsPlaying(false);
    if (!hasFiredEnded.current) {
      hasFiredEnded.current = true;
      const nextCount = playCount + 1;
      setPlayCount(nextCount);
      const keySuffix = attemptId ? `${attemptId}_${id}` : id;
      if (id) localStorage.setItem(`secure_audio_play_count_${keySuffix}`, String(nextCount));
      onEnded?.();
    }
  };


  const formatTime = (t: number) => {
    if (isNaN(t) || t <= 0) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-background/90 p-5 shadow-lg backdrop-blur-md transition-all duration-300 font-sans select-none",
        isLimitReached
          ? "border-muted bg-muted/10 opacity-75"
          : isPlaying
            ? "border-amber-400/40 shadow-amber-500/10 shadow-xl"
            : "border-primary/20 hover:border-primary/30 hover:shadow-xl",
        className
      )}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload="none"
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPause={() => setIsPlaying(false)}
        className="hidden"
      />

      {/* Ambient glow when playing */}
      {isPlaying && (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5 pointer-events-none animate-pulse" />
      )}

      <div className="relative z-10 flex flex-col space-y-4">

        {/* ── Header: status + play count badge ── */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 font-medium">
            <AudioLines
              size={14}
              className={cn(
                "shrink-0 transition-colors",
                isPlaying ? "text-amber-500 animate-pulse" : isLimitReached ? "text-muted-foreground" : "text-primary"
              )}
            />
            <span className="text-muted-foreground text-[11px]">
              {isPlaying
                ? "再生中... / Playing..."
                : isLimitReached
                  ? "再生完了 / Playback Completed"
                  : "音声を聞いてください / Listen to Audio"}
            </span>
          </div>

          {playLimit > 0 && (
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 font-bold tracking-wide uppercase text-[10px] transition-colors",
                isLimitReached
                  ? "bg-muted text-muted-foreground"
                  : playCount > 0
                    ? "bg-amber-500/10 text-amber-600 border border-amber-500/20"
                    : "bg-primary/10 text-primary border border-primary/20"
              )}
            >
              {isLimitReached
                ? "再生不可 / Locked"
                : playCount === 0
                  ? `${playLimit}× 再生可能`
                  : `残り ${playLimit - playCount}× / ${playLimit - playCount} left`}
            </span>
          )}
        </div>

        {/* ── Central row: Play btn + progress + mute ── */}
        <div className="flex items-center gap-4">
          {/* Play/Pause button */}
          <button
            type="button"
            onClick={handleTogglePlay}
            disabled={isLimitReached || isResolving || isPlaying}
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-full shadow-md transition-all duration-200",
              isLimitReached
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : isResolving
                  ? "bg-primary/60 text-primary-foreground cursor-wait"
                  : isPlaying
                    ? "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-amber-500/30 cursor-not-allowed"
                    : "bg-gradient-to-br from-slate-800 to-slate-900 text-white hover:scale-105 active:scale-95 shadow-slate-900/20"
            )}
          >
            {isResolving ? (
              <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : isPlaying ? (
              <Volume2 size={20} className="fill-current animate-pulse" />
            ) : (
              <Play size={20} className="ml-0.5 fill-current" />
            )}
          </button>

          {/* Progress bar + timestamps */}
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex justify-between text-[11px] font-mono text-muted-foreground select-none">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            <div className="relative h-2 w-full rounded-full bg-secondary overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-100 ease-out",
                  isLimitReached
                    ? "bg-muted-foreground/40"
                    : "bg-gradient-to-r from-amber-400 to-orange-500"
                )}
                style={{ width: `${progressPercent}%` }}
              />
              {/* Thumb indicator */}
              {isPlaying && progressPercent > 0 && (
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 border-amber-500 shadow-sm transition-all duration-100"
                  style={{ left: `calc(${progressPercent}% - 6px)` }}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Equalizer bars (playing only) ── */}
        {isPlaying && (
          <div className="flex items-end justify-center gap-[3px] h-5 w-full opacity-70">
            {[0.4, 0.7, 1.0, 0.6, 0.85, 0.5, 0.9, 0.65].map((h, i) => (
              <div
                key={i}
                className="w-1 rounded-full bg-gradient-to-t from-amber-500 to-orange-400 origin-bottom"
                style={{
                  height: `${h * 100}%`,
                  animation: `eq-pulse ${0.5 + (i % 4) * 0.1}s ease-in-out infinite alternate`,
                  animationDelay: `${i * 0.07}s`,
                }}
              />
            ))}
          </div>
        )}

        {/* ── Locked notice ── */}
        {isLimitReached && (
          <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 text-[11px] text-amber-600 font-medium leading-relaxed">
            <AlertCircle size={14} className="shrink-0" />
            <span>この音声は再生済みです。CBT規則により再生できません。<br />
              <span className="text-amber-500/80">This audio is locked after playback per CBT rules.</span></span>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes eq-pulse {
            from { transform: scaleY(0.15); }
            to   { transform: scaleY(1); }
          }
        `
      }} />
    </div>
  );
};
